import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import {
  dismissActionAlert,
  dismissAllActionAlerts,
  listActionAlerts,
  type ActionAlert,
  type ActionAlertsResponse,
} from "@/api/action_alerts";

export function actionAlertsQueryKey(municipalityId: number | undefined) {
  return ["action_alerts", municipalityId] as const;
}

export function summarizeActionAlerts(alerts: ActionAlert[]) {
  return {
    total: alerts.length,
    overdue: alerts.filter((alert) => alert.urgency === "overdue").length,
    urgent: alerts.filter((alert) => alert.urgency === "urgent").length,
    assigned_to_me: alerts.filter((alert) => alert.assigned_to_me).length,
  };
}

export function invalidateActionAlerts(
  queryClient: QueryClient,
  municipalityId: number | undefined,
) {
  if (!municipalityId) return;
  queryClient.invalidateQueries({ queryKey: actionAlertsQueryKey(municipalityId) });
}

export function useActionAlerts(
  municipalityId: number | undefined,
  options?: { poll?: boolean; enabled?: boolean },
) {
  return useQuery({
    queryKey: actionAlertsQueryKey(municipalityId),
    queryFn: () => listActionAlerts(municipalityId!),
    enabled: Boolean(municipalityId) && (options?.enabled ?? true),
    staleTime: 30_000,
    refetchInterval: options?.poll ? 60_000 : false,
  });
}

export function useDismissActionAlert(municipalityId: number | undefined) {
  const queryClient = useQueryClient();
  const queryKey = actionAlertsQueryKey(municipalityId);

  return useMutation({
    mutationFn: (alertKey: string) => dismissActionAlert(municipalityId!, alertKey),
    onMutate: async (alertKey) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ActionAlertsResponse>(queryKey);
      if (previous) {
        const alerts = previous.alerts.filter((alert) => alert.key !== alertKey);
        queryClient.setQueryData<ActionAlertsResponse>(queryKey, {
          alerts,
          summary: summarizeActionAlerts(alerts),
        });
      }
      return { previous };
    },
    onError: (_error, _alertKey, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      invalidateActionAlerts(queryClient, municipalityId);
    },
  });
}

export function useDismissAllActionAlerts(municipalityId: number | undefined) {
  const queryClient = useQueryClient();
  const queryKey = actionAlertsQueryKey(municipalityId);

  return useMutation({
    mutationFn: () => dismissAllActionAlerts(municipalityId!),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ActionAlertsResponse>(queryKey);
      queryClient.setQueryData<ActionAlertsResponse>(queryKey, {
        alerts: [],
        summary: summarizeActionAlerts([]),
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      invalidateActionAlerts(queryClient, municipalityId);
    },
  });
}
